import Cards from "../../../common/Cards";
import Input from "../../../form/Input";
import Select from "../../../form/select/Select";
import DatePicker from "../../../form/DatePicker";
import Button from "../../../form/Button";
import { countries } from "../../../../db/countries";
import {
  initialsValue,
  loadDataOnScroll,
  capitalizeFirst
} from "../../../../helpers";
import { useEffect, useState } from "react";
import AvatarUpload from "../../../common/AvatarUpload";
import useForm from "../../../../hooks/useForm";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { userAddEdit, userDetails } from "../../../../services/userService";
import Form from "../../../form/Form";
import { countryList } from "../../../../services/countryService";
import { languageList } from "../../../../services/LanguageService";
import PhoneNumberInput from "../../../form/PhoneNumberInput";
import { SOCIAL_MEDIA } from "../../../constant/constant";
import Badge from "../../../elements/Badge";
import Image from "../../../elements/Image";
import SelectUpdate from "../../../form/select/SelectUpdate";
import FlexibleSelect from "../../../form/select/FlexibleSelect";

const validation = {
  firstName: { required: true, message: "First name is required" },
  lastName: { required: true, message: "Last name is required" },
  // dob: { required: true, message: "DOB is required" },
  email: { required: true, message: "Email is required" },
  // role: { required: true, message: "Role is required" },
  // phoneCode: { required: true, message: "Phone code is required" },
  // phoneNumber: { required: true, message: "Phone number is required" },
  // timezone: { required: true, message: "Time zone is required" },
};

const UserProfile = ({ roleCode,details, loading, getResponse = () => {} }) => {
  const [keyword, setKeyword] = useState("");
  const [countryKeyword, setCountryKeyword] = useState("");
  const [LanguageKeyword, setLanguageKeyword] = useState("");
  const [countryFilter, setCountryFilter] = useState({
    limit: 300,
    page: 1,
  });
  const [LanguageFilter, setLanguageFilter] = useState({
    limit: 20,
    page: 1,
  });
  const [socialData, setSocialData] = useState([
    {
      name: "",
      url: "",
    },
  ]);

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    resetField,
    formValidate,
  } = useForm({
    validationRules: validation,
  });

  const { data: country } = useQuery({
    queryKey: ["country", countryKeyword, countryFilter],
    queryFn: async () => {
      const res = await countryList({
        ...countryFilter,
        ...(countryKeyword && { keyword: countryKeyword }),
      });
      if (countryFilter?.page > 1) {
        return {
          ...res?.docs,
          data: [...(country?.data || []), ...res?.docs?.data],
        };
      } else {
        return { ...res?.docs };
      }
    },
    placeholderData: keepPreviousData,
  });

  const { data: language } = useQuery({
    queryKey: ["language", LanguageKeyword, LanguageFilter],
    queryFn: async () => {
      const res = await languageList({
        ...LanguageFilter,
        ...(LanguageKeyword && { keyword: LanguageKeyword }),
      });

      if (LanguageFilter?.page > 1) {
        return {
          ...res?.docs,
          data: [...(language?.data || []), ...res?.docs?.data],
        };
      } else {
        return { ...res?.docs };
      }
    },
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (details?._id) {
      delete validation?.confirmPassword;
      delete validation?.password;
      const socialData = details?.socialAccounts
        ? details?.socialAccounts?.map((it) => ({
            url: it?.profileUrl,
            name: it?.provider,
          }))
        : [];
      const address = Array.isArray(details?.address)
        ? details?.address?.[0]
        : details?.address;
      setFieldsValue({
        ...details,
        ...details?.address,
        image: details?.image?.url,
        language: details?.language?._id,
        phoneCode: details?.phone?.countryCode?.replace(/\+/g, ""),
        phoneNumber: details?.phone?.number,
        city: address?.city,
        country: address?.country,
        state: address?.state,
        zipcode: address?.zipcode,
        timezone: details?.preferences?.timezone,
        language: details?.preferences?.language,
      });

      setSocialData(socialData);
    }
    return () => {
      setFieldsValue({});
    };
  }, [details?._id]);

  const {
    mutate: onSubmit,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      const address = {
        city: val?.city,
        country: val?.country,
        state: val?.state,
        zipcode: val?.zipcode,
      };
      const payload = {
        firstName: val?.firstName,
        lastName: val?.lastName,
        email: val?.email,
        countryCode: `+${val?.phoneCode}`,
        phoneNumber: val?.phoneNumber,
        gender: val?.gender,
        image: val?.image,
        role: roleCode,
        dob: val?.dob,
        "preferences[timezone]": val?.timezone,
        "preferences[language]": val?.language,
      };
      const social =
        socialData?.map((item) => ({
          profileUrl: item?.url,
          provider: item?.name,
        })) || [];

      // delete rest?._id;
      return await userAddEdit(
        details?._id
          ? {
              ...(details?._id && {
                id: details?._id,
                ...payload,
                address: [address],
                socialAccounts: social,
              }),
            }
          : {
              ...payload,
              address: [address],
              socialAccounts: social,
            }
      );
    },
    onSuccess: (res) => {
      getResponse(res);
      if (res?.status === 200 || res?.status === 201) {
        resetField();
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const addSocialField = () => {
    setSocialData([
      ...socialData,
      {
        name: "",
        url: "",
      },
    ]);
  };

  const removeSocialField = (index) => {
    setSocialData(socialData?.filter((_, i) => i !== index));
  };
  const handleSocialDataChange = (index, field, value) => {
    const newSocialdata = [...socialData];

    newSocialdata[index][field] = value;

    setSocialData(newSocialdata);
  };

  const matchedCountry = country?.data?.find(
    (cn) => cn?.name === details?.address?.[0]?.country
  );

  const flagWithCountry = matchedCountry
    ? {
        name: matchedCountry?.name,
        image: countries?.find((c) => c?.cca3 === matchedCountry?.iso3)?.flags
          ?.svg,
      }
    : null;

  return (
    <Form onSubmit={onSubmit} handleSubmit={handleSubmit} loading={loading}>
      <div className="flex px-6 py-4 flex-wrap md:flex-nowrap gap-6 items-start">
        <div
          className={`w-full md:w-2/4 flex-shrink-0 ${
            details?._id
              ? "flex items-center justify-center"
              : "flex items-start justify-start"
          }`}
        >
          <AvatarUpload
            src={
              values?.image instanceof File
                ? URL.createObjectURL(values?.image)
                : values?.image
            }
            onChange={(file) => {
              handleChange({ target: { name: "image", value: file } });
            }}
            alt="profile"
            initial={initialsValue(
              details?.firstName + " " + details?.lastName
            )}
          />
        </div>
        {details?._id && (
          <div className="w-full md:w-2/4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 text-sm text-gray-700">
              <div className="flex flex-col gap-2">
                <p className="flex items-center gap-2">
                  <i className="fa-solid fa-envelope text-gray-500"></i>
                  {details?.email || "N/A"}
                </p>
                <p className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-gray-500"></i>+
                  {(details?.phone?.countryCode || "").replace("+", "")}{" "}
                  {details?.phone?.number || ""}
                </p>
                <p className="flex items-center gap-2">
                  <i className="fa-solid fa-earth-asia text-gray-500"></i>

                  <div className="w-7 aspect-1 overflow-hidden rounded-md flex-shrink-0">
                    <Image
                      src={flagWithCountry?.image}
                      alt={flagWithCountry?.name}
                      quality={0.2}
                      className={"w-full h-full object-cover object-center"}
                    />
                  </div>
                  {flagWithCountry?.name || "Country not set"}
                </p>
                <p className="flex items-center gap-2">
                  <i className="fa-solid fa-language text-gray-500"></i>
                  {details?.language || "Language not set"}
                </p>
              </div>

              <div className="flex flex-col gap-2 items-start">
                <Badge
                  type={details?.status === 1 ? "success" : "danger"}
                  label={details?.status === 1 ? "Active" : "Inactive"}
                />
                {/* <p className="flex items-center gap-2 justify-start md:justify-end">
                <i className="fa-solid fa-venus-mars text-gray-500"></i>
                {details?.gender ? (
                  <span
                    className={`px-2 py-0.5 rounded-full text-sm font-medium capitalize
        ${
          details.gender.toLowerCase() === "male"
            ? "bg-blue-100 text-blue-600"
            : details.gender.toLowerCase() === "female"
            ? "bg-pink-100 text-pink-600"
            : "bg-purple-100 text-purple-600"
        }`}
                  >
                    {details.gender}
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-sm font-medium">
                    Gender not set
                  </span>
                )}
              </p> */}

                <p className="flex items-center gap-2 justify-start md:justify-end">
                  <i className="fa-solid fa-cake-candles text-gray-500"></i>
                  {details?.dob
                    ? new Date(details?.dob).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                    : "DOB not set"}
                </p>
                <p className="flex items-center gap-2 justify-start md:justify-end">
                  <i className="fa-solid fa-clock text-gray-500"></i>
                  {details?.preferences?.timezone || "Timezone not set"}
                </p>

                <div className="flex gap-2 flex-wrap justify-start md:justify-end">
                  {(() => {
                    const transformedData = socialData
                      .map((item) => {
                        const match = SOCIAL_MEDIA.find(
                          (media) => media.value === item.name
                        );
                        return match
                          ? {
                              url: item.url,
                              value: match.value,
                              icon: match.icon,
                              label: match.label,
                              class: match.class,
                            }
                          : null;
                      })
                      .filter(Boolean);

                    const visibleItems = transformedData.slice(0, 3);
                    const hiddenItems = transformedData.slice(3);
                    const hiddenCount = hiddenItems.length;

                    return (
                      <>
                        {visibleItems.map((item) => (
                          <a
                            key={item.value}
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={item.label}
                          >
                            <div
                              className={`w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:shadow-md transition-all duration-150 ${item.class}`}
                            >
                              <img
                                src={item.icon}
                                alt={item.label}
                                className="w-6 h-6 object-contain"
                              />
                            </div>
                          </a>
                        ))}

                        {hiddenCount > 0 && (
                          <div className="relative group">
                            <div
                              className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center text-sm font-medium bg-gray-100 text-gray-700 cursor-pointer hover:shadow-md transition-all"
                              title={`${hiddenCount} more`}
                            >
                              +{hiddenCount}
                            </div>

                            {/* Tooltip */}
                            <div className="absolute bottom-full right-0 mb-3 z-20 hidden group-hover:flex flex-wrap gap-2 p-3 bg-white border border-gray-200 shadow-xl rounded-xl w-max max-w-xs">
                              {hiddenItems.map((item) => (
                                <a
                                  key={item.value}
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={item.label}
                                >
                                  <div
                                    className={`w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:shadow-sm ${item.class}`}
                                  >
                                    <img
                                      src={item.icon}
                                      alt={item.label}
                                      className="w-5 h-5 object-contain"
                                    />
                                  </div>
                                </a>
                              ))}
                              <div className="absolute top-full right-2 w-3 h-3 bg-white border-l border-b border-gray-200 rotate-45 z-[-1]" />
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Cards
        title="Basic information"
        description="This section includes essential details and other personal identifiers that help establish your identity."
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 xls:grid-cols-2 gap-3">
            <Input
              label={"First Name"}
              inputPlaceholder="Enter first name"
              onChange={handleChange}
              inputName={"firstName"}
              required={false}
              value={values?.firstName}
              {...formValidate(errors, "firstName")}
            />
            <Input
              label={"Last Name"}
              inputPlaceholder="Enter last name"
              inputName={"lastName"}
              value={values?.lastName}
              onChange={handleChange}
              {...formValidate(errors, "lastName")}
            />
          </div>
          <div className="grid grid-cols-1 xls:grid-cols-2 gap-3">
            <DatePicker
              label={"Select DOB"}
              position="fixed"
              onChange={(e) =>
                handleChange({
                  target: { name: "dob", value: e?.startDate || e?.endDate },
                })
              }
              value={{ startDate: values?.dob, endDate: values?.dob }}
              // {...formValidate(errors, "dob")}
            />
            <FlexibleSelect
              label={"Gender"}
              placeholder={"Select gender"}
              onChange={(e) =>
                handleChange({ target: { name: "gender", value: e } })
              }
              selectedValue={values?.gender}
              dropdownData={[
                { name: "Male", value: "male" },
                { name: "Female", value: "female" },
                { name: "Others", value: "others" },
              ]}
              placement="bottom-start"
              // {...formValidate(errors, "gender")}
            />
          </div>
          <div className="grid grid-cols-1 xls:grid-cols-2 gap-3">
            <FlexibleSelect
              label={"Role"}
              placeholder={"Select role"}
              onChange={(e) =>
                handleChange({ target: { name: "role", value: e } })
              }
              selectedValue={values?.role}
              dropdownData={[
                { name: "Admin", value: "admin" },
                { name: "Waiter", value: "waiter" },
              ]}
              placement="bottom-start"
              // {...formValidate(errors, "gender")}
            />
          </div>
        </div>
      </Cards>
      <Cards
        title="Contact details"
        description="This section contains the information required to reach you."
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 xls:grid-cols-2 gap-3">
            <Input
              label={"Email"}
              inputType={"email"}
              inputPlaceholder="Enter email address"
              inputName={"email"}
              value={values?.email}
              onChange={handleChange}
              {...formValidate(errors, "email")}
            />
            <PhoneNumberInput
              phoneCode={values?.phoneCode}
              phoneNumber={values?.phoneNumber}
              onChangePhoneCode={(e) =>
                handleChange({ target: { name: "phoneCode", value: e } })
              }
              onChangePhoneNumber={(e) =>
                handleChange({
                  target: { name: "phoneNumber", value: e.target.value },
                })
              }
              setCountryKeyword={(k) => {
                setCountryKeyword(k);
              }}
              searchValue={countryKeyword}
              country={country?.data}
              countries={countries}
              // {...formValidate(errors, "phoneNumber")}
              // {...formValidate(errors, "phoneCode")}
              // required={true}
            />
          </div>
          <div className="grid grid-cols-1 xls:grid-cols-2 gap-3">
            {/* <FlexibleSelect
              label={"Country"}
              isSearch
              onSearchHandler={setCountryKeyword}
              placeholder={"Select country"}
              onChange={(e) =>
                handleChange({ target: { name: "country", value: e } })
              }
              selectedValue={values?.country}
              searchValue={countryKeyword}
              dropdownData={country?.data?.map((cn) => {
                return {
                  name: cn?.name,
                  value: cn?.name,
                  image: countries?.find((c) => c?.cca3 === cn?.iso3)?.flags
                    ?.svg,
                };
              })}
              // onScroll={(e) => {
              //   loadDataOnScroll({
              //     event: e,
              //     metadata: country?.metadata,
              //     setData: setCountryFilter,
              //   });
              // }}
              // {...formValidate(errors, "country")}
            /> */}
            <Input
              label={"City"}
              inputPlaceholder="Enter city"
              inputName={"city"}
              value={values?.city}
              onChange={handleChange}
              // {...formValidate(errors, "city")}
            />
            <Input
              label={"State"}
              inputPlaceholder="Enter state"
              inputName={"state"}
              value={values?.state}
              onChange={handleChange}
              // {...formValidate(errors, "state")}
            />
          </div>
          <div className="grid grid-cols-1 xls:grid-cols-2 gap-3">
            <Input
              label={"Zipcode"}
              inputName={"zipcode"}
              value={values?.zipcode}
              inputPlaceholder="Enter zipcode"
              onChange={handleChange}
              // {...formValidate(errors, "zipcode")}
            />
          </div>
          {/* <div className="grid grid-cols-2 gap-3">
            <FlexibleSelect
              label={"Timezone"}
              isSearch
              onSearchHandler={setCountryKeyword}
              placeholder={"Select time zone"}
              onChange={(e) =>
                handleChange({ target: { name: "timezone", value: e } })
              }
              // required={true}
              selectedValue={values?.timezone}
              dropdownData={country?.data?.map((cn) => {
                const timezone =
                  cn?.timezones &&
                  Array.isArray(cn?.timezones) &&
                  cn?.timezones?.length > 0 &&
                  cn?.timezones[0];
                return {
                  name: `${cn?.name}-${timezone?.abbreviation} (${timezone?.gmtOffsetName})`,
                  value: timezone?.gmtOffsetName,
                  image: countries?.find((c) => c?.cca3 === cn?.iso3)?.flags
                    ?.svg,
                };
              })}
              // {...formValidate(errors, "timezone")}
            />
            <FlexibleSelect
              label={"Language"}
              placeholder={"Select language"}
              isSearch
              onSearchHandler={setKeyword}
              onChange={(e) =>
                handleChange({ target: { name: "language", value: e } })
              }
              selectedValue={values?.language}
              dropdownData={language?.data?.map((lang) => {
                return {
                  name: lang?.name,
                  value: lang?.code,
                };
              })}
              onScroll={(e) => {
                loadDataOnScroll({
                  event: e,
                  metadata: language?.metadata,
                  setData: setLanguageFilter,
                });
              }}
              // {...formValidate(errors, "language")}
            />
          </div> */}
        </div>
      </Cards>
      {/* <Cards
        title="Social Media"
        description="Connect with you through your preferred platforms."
      >
        <div className="space-y-6">
          {socialData?.map((item, index) => (
            <div
              key={index}
              className="grid grid-cols-12 gap-4 items-end bg-white border border-slate-200 shadow-sm px-6 py-5 rounded-2xl relative"
            >
              <div className="col-span-12 md:col-span-5">
                <FlexibleSelect
                  isSearch={true}
                  label="Social Platform"
                  placeholder="Select Platform"
                  onChange={(e) => handleSocialDataChange(index, "name", e)}
                  selectedValue={item?.name}
                  dropdownData={SOCIAL_MEDIA?.map((cn) => ({
                    name: cn?.label,
                    value: cn?.value,
                    image: cn?.icon,
                    class: cn?.class,
                  }))}
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <Input
                  label="URL"
                  inputType="url"
                  inputPlaceholder="https://yourprofile.com"
                  inputName={`url-${index}`}
                  value={item.url}
                  onChange={(e) =>
                    handleSocialDataChange(index, "url", e.target.value)
                  }
                />
              </div>
              <div className="col-span-12 md:col-span-1 flex justify-end md:justify-center">
                {socialData?.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSocialField(index)}
                    className="text-rose-500 hover:text-rose-600 transition duration-200"
                    title="Remove"
                  >
                    <i className="fa-solid fa-trash text-lg"></i>
                  </button>
                )}
              </div>
            </div>
          ))}

          <div className="text-center">
            <Button
              buttonLabel="Add More"
              buttonClasses="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg shadow-md transition duration-200"
              buttonLabelClasses="font-medium"
              buttonFunction={addSocialField}
            />
          </div>
        </div>
      </Cards> */}
      <div className="pt-3 pb-6 flex justify-end">
        <Button
          buttonType="submit"
          buttonClasses={"min-w-36"}
          buttonLabel={details?._id ? `Update ${capitalizeFirst(roleCode)}` : `Save ${capitalizeFirst(roleCode)}`}
          {...((isLoading || loading) && {
            loading: true,
            buttonIconPosition: "left",
            isDisable: true,
          })}
        />
      </div>
    </Form>
  );
};

export default UserProfile;
